import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, EventEmitter, Component, ChangeDetectionStrategy, Input, Output, HostBinding } from '@angular/core';
import { isElementDataEmpty, getElementSampleData, getSlideOptionValue } from '@slidebean/sdk/presentation';
import { ColorSet } from '@slidebean/color';
import { Subject } from 'rxjs';

const RENDER_PLUGIN = new InjectionToken('__SB_RENDER_PLUGIN_TOKEN__');

class RenderPluginCatalog {
    constructor(plugins = []) {
        this.plugins = plugins.reduce((map, obj) => ((map[obj.type] = obj), map), {});
    }
    get(type) {
        return typeof type === 'string' ? this.plugins[type.toLowerCase()] : void 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderPluginCatalog, deps: [{ token: RENDER_PLUGIN, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderPluginCatalog, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderPluginCatalog, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [RENDER_PLUGIN]
                }] }] });

function didChange(change) {
    return (change &&
        !change.isFirstChange() &&
        change.previousValue !== change.currentValue);
}
class SlideElementComponent {
    constructor() {
        this.isActive = false;
        this.onActivationEnded = new EventEmitter();
        this.reloadStyles$ = new Subject();
        this.reloadData$ = new Subject();
        this.destroy$ = new Subject();
        // We need to add an object in the component instance to cache the calculated element data
        // for empty elements. If we create a new object literal each time the element data is calculated
        // a performance regression is experienced.
        this.cachedEmptyElementData = {};
    }
    get elementId() {
        return this.element ? this.element.id : void 0;
    }
    get data() {
        if (this.element) {
            return isElementDataEmpty(this.element)
                ? Object.assign(this.cachedEmptyElementData, this.element.data, getElementSampleData(this.element.type))
                : this.element.data;
        }
        return {};
    }
    ngOnChanges(changes) {
        if (didChange(changes['slideOptions']) ||
            didChange(changes['element']) ||
            didChange(changes['elementData']) ||
            didChange(changes['elementOptions']) ||
            didChange(changes['colorSet']) ||
            didChange(changes['headingFont'])) {
            if (didChange(changes['colorSet']))
                this.colors = new ColorSet(changes['colorSet'].currentValue);
            this.reloadStyles$.next();
        }
        if (didChange(changes['elementData'])) {
            this.reloadData$.next();
        }
        if (didChange(changes['isActive'])) {
            if (this.isActive) {
                this.activate();
            }
            else {
                this.deactivate();
            }
        }
    }
    ngOnInit() {
        this.reloadStyles$
            // .pipe(debounceTime(10))
            .subscribe(() => this.reloadStyles());
        this.reloadData$.subscribe(() => this.reloadData());
        this.colors = new ColorSet(this.colorSet);
        // Trigger initial load
        this.reloadStyles();
        this.reloadData();
    }
    ngOnDestroy() {
        this.destroy$.next();
    }
    /* eslint-disable */
    reloadStyles() { }
    reloadData() { }
    activate() { }
    deactivate() { }
    getSlideOptionValue(option) {
        return getSlideOptionValue(this.slide)(option);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideElementComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlideElementComponent, selector: "sb-slide-element", inputs: { slide: "slide", slideOptions: "slideOptions", element: "element", elementData: "elementData", elementOptions: "elementOptions", colorSet: "colorSet", textFont: "textFont", headingFont: "headingFont", isActive: "isActive" }, outputs: { onActivationEnded: "onActivationEnded" }, host: { properties: { "attr.data-sb-slide-element-id": "this.elementId" } }, usesOnChanges: true, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideElementComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-slide-element',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { slide: [{
                type: Input
            }], slideOptions: [{
                type: Input
            }], element: [{
                type: Input
            }], elementData: [{
                type: Input
            }], elementOptions: [{
                type: Input
            }], colorSet: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], isActive: [{
                type: Input
            }], onActivationEnded: [{
                type: Output
            }], elementId: [{
                type: HostBinding,
                args: ['attr.data-sb-slide-element-id']
            }] } });

class SnapPoints {
    constructor(parent, target, slideElements) {
        this.snapRects = [];
        this.GRID_X_FACTOR = 1 / (16 * 2);
        this.GRID_Y_FACTOR = 1 / (10 * 2);
        this.load = (slide, target, slideElements) => {
            this.snapRects = [
                {
                    left: 0,
                    right: 1,
                    top: 0,
                    bottom: 1,
                    centerX: 0.5,
                    centerY: 0.5,
                },
            ];
            // Check for other elements and add their snap points
            slideElements
                .filter(el => el !== target)
                .forEach((el) => {
                this.snapRects.push(...this.extractSnapRects(slide, el));
            });
        };
        this.rectToSnapRect = (slideRect) => (elRect) => {
            const left = (elRect.left - slideRect.left) / slideRect.width;
            const right = (elRect.right - slideRect.left) / slideRect.width;
            const top = (elRect.top - slideRect.top) / slideRect.height;
            const bottom = (elRect.bottom - slideRect.top) / slideRect.height;
            return {
                left,
                right,
                top,
                bottom,
                centerX: (left + right) / 2,
                centerY: (top + bottom) / 2,
            };
        };
        this.snapToGrid = (factor) => (x) => Math.round(x / factor) * factor;
        this.snapToX = this.snapToGrid(this.GRID_X_FACTOR);
        this.snapToY = this.snapToGrid(this.GRID_Y_FACTOR);
        this.extractSnapRects = (slide, target) => {
            const toSnapRect = this.rectToSnapRect(slide.getBoundingClientRect());
            const mainSnapRect = toSnapRect(target.getBoundingClientRect());
            return [
                mainSnapRect,
                ...Array.from(target.querySelectorAll('*[data-slide-element-snap]')).map((innerEl) => {
                    const rect = toSnapRect(innerEl.getBoundingClientRect());
                    return Object.assign(rect, {
                        offsetX: rect.left - mainSnapRect.left,
                        offsetY: rect.top - mainSnapRect.top,
                    });
                }),
            ];
        };
        this.find = ({ targetSnapRects, checkCenters = true, }) => {
            const result = { xs: [], ys: [] };
            const isClose = (maxDistance) => (a) => (b) => Math.abs(a - b) <= maxDistance;
            const isCloseX = isClose(this.GRID_X_FACTOR / 4);
            const isCloseY = isClose(this.GRID_Y_FACTOR / 4);
            const bordersX = this.snapRects.reduce((arr, { left, right }) => [...arr, left, right], []);
            const bordersY = this.snapRects.reduce((arr, { top, bottom }) => [...arr, top, bottom], []);
            const centersX = this.snapRects.reduce((arr, { centerX }) => [...arr, centerX], []);
            const centersY = this.snapRects.reduce((arr, { centerY }) => [...arr, centerY], []);
            for (const { left, right, top, bottom, centerX, centerY, offsetX = 0, offsetY = 0, } of targetSnapRects) {
                let match;
                // X
                match = bordersX.find(isCloseX(left));
                if (match !== undefined) {
                    result.xs.push({ value: match - offsetX, displayValue: match });
                }
                match = bordersX.find(isCloseX(right));
                if (match !== undefined) {
                    result.xs.push({
                        value: match - (right - left) - offsetX,
                        displayValue: match,
                    });
                }
                if (checkCenters) {
                    match = centersX.find(isCloseX(centerX));
                    if (match !== undefined) {
                        result.xs.push({
                            value: match - (centerX - left) - offsetX,
                            displayValue: match,
                        });
                    }
                }
                // Y
                match = bordersY.find(isCloseY(top));
                if (match !== undefined) {
                    result.ys.push({ value: match - offsetY, displayValue: match });
                }
                match = bordersY.find(isCloseY(bottom));
                if (match !== undefined) {
                    result.ys.push({
                        value: match - (bottom - top) - offsetY,
                        displayValue: match,
                    });
                }
                if (checkCenters) {
                    match = centersY.find(isCloseY(centerY));
                    if (match !== undefined) {
                        result.ys.push({
                            value: match - (centerY - top) - offsetY,
                            displayValue: match,
                        });
                    }
                }
            }
            return result;
        };
        this.load(parent, target, slideElements);
    }
}

class Draggable {
    init(target, slide, slideElements, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    slideElement) {
        const x = target.offsetLeft / slide.offsetWidth;
        const y = target.offsetTop / slide.offsetHeight;
        target.setAttribute('data-x', `${x}`);
        target.setAttribute('data-y', `${y}`);
        target.setAttribute('data-raw-x', `${x}`);
        target.setAttribute('data-raw-y', `${y}`);
        this.snapPoints = new SnapPoints(slide, target, slideElements);
    }
    onDrag(slide, target, dx, dy, snap = false, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    slideElement) {
        let x = +(target.getAttribute('data-raw-x') || '0');
        let y = +(target.getAttribute('data-raw-y') || '0');
        const snapResult = {
            xs: [],
            ys: [],
        };
        if (snap) {
            x = this.snapPoints.snapToX(x);
            y = this.snapPoints.snapToY(y);
        }
        else {
            target.style.left = `${x * 100}%`;
            target.style.top = `${y * 100}%`;
            // Snap to other elements
            const snapPoints = this.snapPoints.find({
                targetSnapRects: this.snapPoints.extractSnapRects(slide, target),
            });
            snapResult.xs = snapPoints.xs.map(({ displayValue }) => displayValue);
            snapResult.ys = snapPoints.ys.map(({ displayValue }) => displayValue);
            if (snapPoints.xs.length) {
                const anySnapPointX = snapPoints.xs[0];
                x = anySnapPointX.value;
            }
            if (snapPoints.ys.length) {
                const anySnapPointY = snapPoints.ys[0];
                y = anySnapPointY.value;
            }
        }
        const currentX = +(target.getAttribute('data-x') || '0');
        const currentY = +(target.getAttribute('data-y') || '0');
        dx = x - currentX;
        dy = y - currentY;
        x = currentX + dx;
        y = currentY + dy;
        target.setAttribute('data-x', `${x}`);
        target.setAttribute('data-y', `${y}`);
        target.style.left = `${x * 100}%`;
        target.style.top = `${y * 100}%`;
        return {
            snapPoints: snapResult,
            requireChangeDetection: false,
            data: { position: { x, y } },
        };
    }
    end(target) {
        target.removeAttribute('data-x');
        target.removeAttribute('data-y');
        target.removeAttribute('data-raw-x');
        target.removeAttribute('data-raw-y');
    }
}

class Resizable {
    init(target, slide, slideElements) {
        const x = target.offsetLeft / slide.offsetWidth;
        const y = target.offsetTop / slide.offsetHeight;
        target.setAttribute('data-x', `${x}`);
        target.setAttribute('data-y', `${y}`);
        target.setAttribute('data-raw-x', `${x}`);
        target.setAttribute('data-raw-y', `${y}`);
        this.snapPoints = new SnapPoints(slide, target, slideElements);
    }
    onResize(target, slide, snap = false, eventRect) {
        const snapResult = {
            xs: [],
            ys: [],
        };
        if (!slide)
            return { data: {} };
        const ratio = target.offsetWidth / (target.getBoundingClientRect().width || 1);
        const currentX = +(target.getAttribute('data-x') || '0');
        const currentY = +(target.getAttribute('data-y') || '0');
        const dx = (eventRect.left * ratio) / slide.offsetWidth;
        const dy = (eventRect.top * ratio) / slide.offsetHeight;
        let x = currentX + dx;
        let y = currentY + dy;
        let w = (eventRect.width * ratio) / slide.offsetWidth;
        let h = (eventRect.height * ratio) / slide.offsetHeight;
        target.setAttribute('data-x', x.toString());
        target.setAttribute('data-y', y.toString());
        if (snap) {
            x = this.snapPoints.snapToX(x);
            y = this.snapPoints.snapToY(y);
            w = this.snapPoints.snapToX(x + w) - x;
            h = this.snapPoints.snapToY(y + h) - y;
        }
        else {
            // Change the style so that inner elements can be measured
            target.style.left = `${x * 100}%`;
            target.style.top = `${y * 100}%`;
            target.style.width = `${w * 100}%`;
            target.style.height = `${h * 100}%`;
            const snapPoints = this.snapPoints.find({
                targetSnapRects: this.snapPoints.extractSnapRects(slide, target),
            });
            snapResult.xs = snapPoints.xs.map(({ displayValue }) => displayValue);
            snapResult.ys = snapPoints.ys.map(({ displayValue }) => displayValue);
            if (snapPoints.xs.length) {
                const anySnapPointX = snapPoints.xs[0];
                if (dx) {
                    w += x - anySnapPointX.value;
                    x = anySnapPointX.value;
                }
                else {
                    w += anySnapPointX.value - x;
                }
            }
            if (snapPoints.ys.length) {
                const anySnapPointY = snapPoints.ys[0];
                if (dy) {
                    h += y - anySnapPointY.value;
                    y = anySnapPointY.value;
                }
                else {
                    h += anySnapPointY.value - y;
                }
            }
        }
        target.style.left = `${x * 100}%`;
        target.style.top = `${y * 100}%`;
        target.style.width = `${w * 100}%`;
        target.style.height = `${h * 100}%`;
        return {
            snapPoints: snapResult,
            data: { position: { x, y, w, h } },
        };
    }
    end(target) {
        target.removeAttribute('data-x');
        target.removeAttribute('data-y');
        target.removeAttribute('data-raw-x');
        target.removeAttribute('data-raw-y');
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { Draggable, RENDER_PLUGIN, RenderPluginCatalog, Resizable, SlideElementComponent, SnapPoints };
